<ng-container *transloco="let t">
  <ld-base-modal
    [title]="
      currentStep() === 'review'
        ? t('modalsCreditCardReviewTitle')
        : currentStep() === 'complete'
          ? t('modalsCreditCardConfirmationTitle')
          : t('modalsCreditCardStepsForm')
    "
    [size]="'large'"
    [embedded]="embedded()"
    (closeModal)="onClose()"
  >
    <div modal-content>
      @if (currentStep() === 'form') {
        <form [formGroup]="cardForm" class="flex flex-col gap-5">
          <p class="font-dm text-base text-ld-neutral">
            {{ t('modalsCreditCardPaymentAmountLabel') }} {{ amount() | currency }}
          </p>

          <div class="grid grid-cols-2 gap-4">
            <mat-form-field id="first-name-field" appearance="outline" class="w-full">
              <mat-label>{{ t('commonFirstName') }}</mat-label>
              <input matInput id="firstName" type="text" formControlName="firstName" />
              @if (cardForm.get('firstName')?.hasError('required')) {
                <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field id="last-name-field" appearance="outline" class="w-full">
              <mat-label>{{ t('commonLastName') }}</mat-label>
              <input matInput id="lastName" type="text" formControlName="lastName" />
              @if (cardForm.get('lastName')?.hasError('required')) {
                <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
              }
            </mat-form-field>
          </div>

          <div class="grid grid-cols-[2fr_1fr] gap-4">
            <mat-form-field id="card-number-field" appearance="outline" class="w-full">
              <mat-label>{{ t('cardNumber') }}</mat-label>
              <input
                matInput
                id="cardNumber"
                type="text"
                formControlName="cardNumber"
                (input)="onCardNumberInput($event)"
                maxlength="19"
                glance_masked="true"
              />
              @if (cardForm.get('cardNumber')?.hasError('required')) {
                <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field id="cvv-field" appearance="outline" class="w-full">
              <mat-label>{{ t('securityCode') }}</mat-label>
              <input matInput id="cvv" type="password" formControlName="cvv" maxlength="4" glance_masked="true" />
              @if (cardForm.get('cvv')?.hasError('required')) {
                <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
              }
            </mat-form-field>
          </div>

          <div class="flex items-center gap-1 -mt-2">
            <img src="assets/images/card-mastercard.svg" alt="Mastercard" class="h-6" />
            <img src="assets/images/card-visa.svg" alt="Visa" class="h-6" />
            <img src="assets/images/card-amex.svg" alt="Amex" class="h-6" />
            <img src="assets/images/card-discover.svg" alt="Discover" class="h-6" />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <mat-form-field id="expiration-month-field" appearance="outline" class="w-full">
              <mat-label>{{ t('expirationMonth') }}</mat-label>
              <mat-select id="expirationMonth" formControlName="expirationMonth">
                @for (month of months; track month.value) {
                  <mat-option [value]="month.value">{{ month.display }}</mat-option>
                }
              </mat-select>
              @if (cardForm.get('expirationMonth')?.hasError('required')) {
                <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field id="expiration-year-field" appearance="outline" class="w-full">
              <mat-label>{{ t('expirationYear') }}</mat-label>
              <mat-select id="expirationYear" formControlName="expirationYear">
                @for (year of years(); track year) {
                  <mat-option [value]="year">{{ year }}</mat-option>
                }
              </mat-select>
              @if (cardForm.get('expirationYear')?.hasError('required')) {
                <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
              }
            </mat-form-field>
          </div>

          <mat-form-field id="address-field" appearance="outline" class="w-full">
            <mat-label>{{ t('modalsCreditCardBillingAddress') }}</mat-label>
            <input matInput id="address" type="text" formControlName="address" />
            @if (cardForm.get('address')?.hasError('required')) {
              <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
            }
          </mat-form-field>

          <div class="grid grid-cols-[2fr_1fr_1fr] gap-4">
            <mat-form-field id="city-field" appearance="outline" class="w-full">
              <mat-label>{{ t('commonFieldsCity') }}</mat-label>
              <input matInput id="city" type="text" formControlName="city" />
              @if (cardForm.get('city')?.hasError('required')) {
                <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field id="state-field" appearance="outline" class="w-full">
              <mat-label>{{ t('commonFieldsState') }}</mat-label>
              <mat-select id="state" formControlName="state">
                @for (st of states; track st) {
                  <mat-option [value]="st">{{ st }}</mat-option>
                }
              </mat-select>
              @if (cardForm.get('state')?.hasError('required')) {
                <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field id="zip-field" appearance="outline" class="w-full">
              <mat-label>{{ t('commonFieldsZipCode') }}</mat-label>
              <input matInput id="zip" type="text" formControlName="zip" maxlength="5" (blur)="onZipChange()" />
              @if (cardForm.get('zip')?.hasError('required')) {
                <mat-error>{{ t('commonLabelsRequired') }}</mat-error>
              }
            </mat-form-field>
          </div>

          @if (errorMessage()) {
            <div class="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p class="text-sm font-dm text-red-700">{{ errorMessage() }}</p>
            </div>
          }
        </form>
      }

      @if (currentStep() === 'review') {
        <div class="space-y-4">
          <div class="divide-y divide-ld-neutral-200">
            <div id="payment-review-amount" class="flex justify-between py-3">
              <span class="font-dm text-sm text-ld-neutral-500">{{ t('modalsCreditCardReviewAmount') }}</span>
              <span class="font-dm text-sm font-medium text-ld-neutral">{{ amount() | currency }}</span>
            </div>
            <div id="payment-review-date" class="flex justify-between py-3">
              <span class="font-dm text-sm text-ld-neutral-500">{{ t('modalsCreditCardReviewDateOfPayment') }}</span>
              <span class="font-dm text-sm font-medium text-ld-neutral">{{ paymentDate() | date: 'MM/dd/yyyy' }}</span>
            </div>
            <div id="payment-review-name" class="flex justify-between py-3">
              <span class="font-dm text-sm text-ld-neutral-500">{{ t('modalsCreditCardReviewNameOnCard') }}</span>
              <span class="font-dm text-sm font-medium text-ld-neutral">{{ nameOnCard }}</span>
            </div>
            <div id="payment-review-card" class="flex justify-between py-3 border-b border-ld-neutral-200">
              <span class="font-dm text-sm text-ld-neutral-500">{{ t('modalsCreditCardReviewCardNumber') }}</span>
              <span class="font-dm text-sm font-medium text-ld-neutral">{{ maskedCardNumber }}</span>
            </div>
          </div>

          <div class="pt-2">
            <mat-checkbox
              id="payment-review-consent-checkbox"
              [checked]="consentChecked()"
              (change)="onConsentChange($event.checked)"
              color="primary"
              class="font-dm text-sm leading-snug"
            >
              <span class="text-sm font-dm text-ld-neutral leading-snug">
                {{ t('modalsCreditCardReviewConsentText', { amount: (amount() | currency) }) }}
              </span>
            </mat-checkbox>
          </div>

          <p id="payment-review-certification" class="text-sm font-dm text-ld-neutral leading-snug">
            {{ t('modalsCreditCardReviewCertificationText') }}
          </p>
        </div>
      }

      @if (currentStep() === 'processing') {
        <div class="flex flex-col items-center justify-center py-12">
          <mat-spinner diameter="48" class="mb-4"></mat-spinner>
          <p class="font-dm text-lg text-ld-neutral">{{ t('commonMessagesProcessingPayment') }}</p>
          <p class="font-dm text-sm text-ld-neutral-500 mt-2">{{ t('commonMessagesDoNotClose') }}</p>
        </div>
      }

      @if (currentStep() === 'complete') {
        <div class="space-y-4">
          <p id="payment-confirmation-success" class="font-dm text-sm text-ld-neutral">
            {{ t('modalsCreditCardConfirmationSuccess') }}
          </p>

          <p id="payment-confirmation-appraisal" class="font-dm text-sm text-ld-neutral">
            {{ t('modalsCreditCardConfirmationAppraisalMessage') }}
          </p>

          <div class="divide-y divide-ld-neutral-200">
            @if (paymentResponse()?.transactionId) {
              <div id="payment-confirmation-number" class="flex justify-between py-3">
                <span class="font-dm text-sm text-ld-neutral-500">{{ t('modalsCreditCardConfirmationNumber') }}</span>
                <span class="font-dm text-sm font-medium text-ld-neutral">{{ paymentResponse()?.transactionId }}</span>
              </div>
            }
            <div id="payment-confirmation-amount" class="flex justify-between py-3">
              <span class="font-dm text-sm text-ld-neutral-500">{{ t('modalsCreditCardReviewAmount') }}</span>
              <span class="font-dm text-sm font-medium text-ld-neutral">{{ amount() | currency }}</span>
            </div>
            <div id="payment-confirmation-date" class="flex justify-between py-3">
              <span class="font-dm text-sm text-ld-neutral-500">{{ t('modalsCreditCardReviewDateOfPayment') }}</span>
              <span class="font-dm text-sm font-medium text-ld-neutral">{{ paymentDate() | date: 'MM/dd/yyyy' }}</span>
            </div>
            <div id="payment-confirmation-name" class="flex justify-between py-3">
              <span class="font-dm text-sm text-ld-neutral-500">{{ t('modalsCreditCardReviewNameOnCard') }}</span>
              <span class="font-dm text-sm font-medium text-ld-neutral">{{ nameOnCard }}</span>
            </div>
            <div id="payment-confirmation-card" class="flex justify-between py-3 border-b border-ld-neutral-200">
              <span class="font-dm text-sm text-ld-neutral-500">{{ t('modalsCreditCardReviewCardNumber') }}</span>
              <span class="font-dm text-sm font-medium text-ld-neutral">{{ maskedCardNumber }}</span>
            </div>
          </div>
        </div>
      }

      @if (currentStep() === 'error') {
        <div class="flex flex-col items-center justify-center py-8">
          <img [src]="errorIcon" alt="Error" class="w-12 h-12 mb-4" />
          <h3 class="font-dm text-xl font-semibold text-ld-neutral mb-2">{{ t('modalsCreditCardPaymentFailed') }}</h3>
          <p class="font-dm text-sm text-ld-neutral-500 text-center mb-4">
            {{ errorMessage() || t('commonCreditCardStepsError') }}
          </p>
        </div>
      }
    </div>
    <div modal-footer>
      @switch (currentStep()) {
        @case ('form') {
          <button id="credit-card-cancel-btn" type="button" class="btn-outline-ld-purple" (click)="onClose()">
            {{ t('commonButtonsCancel') }}
          </button>
          <button
            id="credit-card-review-btn"
            type="button"
            class="btn-primary-ld-purple"
            [disabled]="!cardForm.valid || isLoading()"
            (click)="onReview()"
          >
            @if (isLoading()) {
              <mat-spinner diameter="16" class="!w-4 !h-4 [&_circle]:!stroke-white"></mat-spinner>
            }
            {{ t('modalsCreditCardReviewPaymentButton') }}
          </button>
        }
        @case ('review') {
          <button id="credit-card-edit-btn" type="button" class="btn-outline-ld-purple" (click)="onBackToForm()">
            {{ t('modalsCreditCardReviewEdit') }}
          </button>
          <button
            id="credit-card-make-payment-btn"
            type="button"
            class="btn-primary-ld-purple"
            [disabled]="!consentChecked()"
            (click)="onSubmitPayment()"
          >
            {{ t('modalsCreditCardReviewMakePayment') }}
          </button>
        }
        @case ('complete') {
          <button id="credit-card-close-btn" type="button" class="btn-primary-ld-purple" (click)="onClose()">
            {{ t('commonAriaClose') }}
          </button>
        }
        @case ('error') {
          <button id="credit-card-close-error-btn" type="button" class="btn-outline-ld-purple" (click)="onClose()">
            {{ t('commonAriaClose') }}
          </button>
          <button id="credit-card-retry-btn" type="button" class="btn-primary-ld-purple" (click)="onRetry()">
            {{ t('commonTryAgain') }}
          </button>
        }
      }
    </div>
  </ld-base-modal>
</ng-container>
